import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Heart, Activity, Target, TrendingUp, Smartphone, Watch, Zap, Plus, CheckCircle } from 'lucide-react';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';

const HealthIntegration: React.FC = () => {
  const [connectedDevices, setConnectedDevices] = useState([
    { id: 1, name: 'Apple Watch', type: 'smartwatch', connected: true, lastSync: '2 minutes ago' },
    { id: 2, name: 'MyFitnessPal', type: 'app', connected: true, lastSync: '1 hour ago' },
  ]);

  const [availableIntegrations] = useState([
    { id: 3, name: 'Fitbit', type: 'smartwatch', icon: '⌚', description: 'Sync heart rate, steps, and sleep data' },
    { id: 4, name: 'Google Fit', type: 'app', icon: '📱', description: 'Track activities and health metrics' },
    { id: 5, name: 'Samsung Health', type: 'app', icon: '📊', description: 'Comprehensive health monitoring' },
    { id: 6, name: 'Garmin', type: 'smartwatch', icon: '⌚', description: 'Advanced fitness tracking' },
    { id: 7, name: 'Strava', type: 'app', icon: '🏃', description: 'Activity tracking and social features' },
    { id: 8, name: 'Oura Ring', type: 'wearable', icon: '💍', description: 'Sleep and recovery insights' },
  ]);

  const healthMetrics = [
    { 
      title: 'Heart Rate', 
      value: '72  BPM', 
      trend: '+2%', 
      icon: <Heart className="w-6 h-6" />, 
      color: 'from-red-500 to-pink-500',
      status: 'normal'
    },
    { 
      title: 'Daily Steps', 
      value: '8,542', 
      trend: '+15%', 
      icon: <Activity className="w-6 h-6" />, 
      color: 'from-blue-500 to-cyan-500',
      status: 'good'
    },
    { 
      title: 'Calories Burned', 
      value: '2,340', 
      trend: '+8%', 
      icon: <Zap className="w-6 h-6" />, 
      color: 'from-orange-500 to-red-500',
      status: 'excellent'
    },
    { 
      title: 'Sleep Quality', 
      value: '87%', 
      trend: '+5%', 
      icon: <Target className="w-6 h-6" />, 
      color: 'from-purple-500 to-indigo-500',
      status: 'good'
    },
  ];

  const nutritionGoals = [
    { name: 'Daily Calories', current: 1850, target: 2000, unit: 'kcal' },
    { name: 'Protein', current: 120, target: 150, unit: 'g' },
    { name: 'Water Intake', current: 6, target: 8, unit: 'glasses' },
    { name: 'Fiber', current: 22, target: 30, unit: 'g' },
  ];

  const connectDevice = (deviceId: number) => {
    const device = availableIntegrations.find(d => d.id === deviceId);
    if (device) {
      setConnectedDevices([...connectedDevices, {
        id: device.id,
        name: device.name,
        type: device.type,
        connected: true,
        lastSync: 'Just now'
      }]);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'excellent': return 'text-green-400';
      case 'good': return 'text-blue-400';
      case 'normal': return 'text-yellow-400';
      default: return 'text-gray-400';
    }
  };

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-pink-500 to-rose-500 rounded-full flex items-center justify-center">
              <Heart className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Health <span className="bg-gradient-to-r from-pink-400 to-rose-400 bg-clip-text text-transparent">
              Integration
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Seamlessly connect your fitness devices and health apps for comprehensive wellness tracking 
            with AI-powered insights and personalized recommendations.
          </p>
        </motion.div>

        {/* Health Metrics Overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          {healthMetrics.map((metric, index) => (
            <FloatingElement key={metric.title} delay={index * 0.1}>
              <GlassCard className="p-6 text-center" glow>
                <div className={`w-12 h-12 mx-auto mb-4 bg-gradient-to-r ${metric.color} rounded-full flex items-center justify-center text-white`}>
                  {metric.icon}
                </div>
                <h3 className="text-white font-semibold mb-2">{metric.title}</h3>
                <div className="text-3xl font-bold text-white mb-2">{metric.value}</div>
                <div className="flex items-center justify-center space-x-2">
                  <TrendingUp className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-sm font-medium">{metric.trend}</span>
                </div>
                <div className={`text-sm mt-2 ${getStatusColor(metric.status)} capitalize`}>
                  {metric.status}
                </div>
              </GlassCard>
            </FloatingElement>
          ))}
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
          {/* Connected Devices */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <CheckCircle className="w-6 h-6 text-green-400 mr-2" />
                Connected Devices
              </h2>
              <div className="space-y-4">
                {connectedDevices.map((device) => (
                  <div key={device.id} className="flex items-center justify-between p-4 bg-white/5 rounded-xl border border-white/10">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                        {device.type === 'smartwatch' ? <Watch className="w-5 h-5 text-white" /> : <Smartphone className="w-5 h-5 text-white" />}
                      </div>
                      <div>
                        <div className="text-white font-medium">{device.name}</div>
                        <div className="text-purple-300 text-sm">Last sync: {device.lastSync}</div>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-green-400 text-sm font-medium">Connected</span>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>

          {/* Nutrition Goals */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
          >
            <GlassCard className="p-6">
              <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                <Target className="w-6 h-6 text-purple-400 mr-2" />
                Daily Nutrition Goals
              </h2>
              <div className="space-y-4">
                {nutritionGoals.map((goal, index) => (
                  <div key={goal.name} className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-white font-medium">{goal.name}</span>
                      <span className="text-purple-300 text-sm">
                        {goal.current} / {goal.target} {goal.unit}
                      </span>
                    </div>
                    <div className="w-full bg-gray-700 rounded-full h-3">
                      <motion.div
                        className="h-3 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"
                        initial={{ width: 0 }}
                        animate={{ width: `${Math.min((goal.current / goal.target) * 100, 100)}%` }}
                        transition={{ duration: 1, delay: 0.5 + index * 0.1 }}
                      />
                    </div>
                    <div className="text-right">
                      <span className={`text-sm font-medium ${
                        goal.current >= goal.target ? 'text-green-400' : 
                        goal.current >= goal.target * 0.8 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {Math.round((goal.current / goal.target) * 100)}%
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </GlassCard>
          </motion.div>
        </div>

        {/* Available Integrations */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <Plus className="w-6 h-6 text-cyan-400 mr-2" />
              Available Integrations
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {availableIntegrations
                .filter(integration => !connectedDevices.some(device => device.id === integration.id))
                .map((integration) => (
                <motion.div
                  key={integration.id}
                  whileHover={{ scale: 1.02 }}
                  className="p-6 bg-white/5 rounded-xl border border-white/10 hover:border-purple-500/30 transition-all duration-200 cursor-pointer"
                  onClick={() => connectDevice(integration.id)}
                >
                  <div className="flex items-start space-x-4">
                    <div className="text-3xl">{integration.icon}</div>
                    <div className="flex-1">
                      <h3 className="text-white font-semibold mb-2">{integration.name}</h3>
                      <p className="text-purple-300 text-sm mb-4">{integration.description}</p>
                      <button className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200">
                        Connect
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </GlassCard>
        </motion.div>

        {/* AI Insights */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-8"
        >
          <GlassCard className="p-6">
            <h2 className="text-xl font-bold text-white mb-6 flex items-center">
              <Zap className="w-6 h-6 text-yellow-400 mr-2" />
              AI Health Insights
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4">
                <h3 className="text-blue-300 font-semibold mb-2">💡 Recommendation</h3>
                <p className="text-blue-200 text-sm">
                  Your heart rate variability suggests good recovery. Consider increasing workout intensity by 10% this week.
                </p>
              </div>
              
              <div className="bg-green-500/10 border border-green-500/30 rounded-xl p-4">
                <h3 className="text-green-300 font-semibold mb-2">✅ Achievement</h3>
                <p className="text-green-200 text-sm">
                  Excellent protein intake this week! You've consistently met 95% of your daily protein goals.
                </p>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4">
                <h3 className="text-yellow-300 font-semibold mb-2">⚠️ Alert</h3>
                <p className="text-yellow-200 text-sm">
                  Sleep quality has decreased by 12% this week. Consider adjusting your bedtime routine.
                </p>
              </div>
            </div>
          </GlassCard>
        </motion.div>
      </div>
    </div>
  );
};

export default HealthIntegration;